import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import BlankCard from 'assets/images/blankCard.png';
import { EQueryFetchPolicy } from 'constants/common';
import { CardTypeOptions, ETutorialStatus } from 'constants/graphqlTypes';
import { TEST_PROFILE_NO_PICKS_TITLE_LABEL } from 'constants/aqa/profile';

import useTheme from 'helpers/useTheme';
import usePageType from 'helpers/usePageType';
import useGuideTour from 'helpers/useGuideTour';
import { useAnalytics } from 'contexts/AnalyticsContext';

import { useGetTotalCountCards, useGetUserId, useProfile } from 'graphQL/profile/hooks';
import { useUserSettings } from 'graphQL/contextualIntro/hooks';

import AppTour from 'components/Profile/AppTour';
import ProfileTop from 'components/Profile/ProfileTop';
import { StyledNoContent } from 'components/Profile/styled';
import { ROUTE_DISCOVER_FEATURED } from 'routes';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { StyledImageWrap, StyledProfileContainer } from './styled';

const ProfilePage: FC = () => {
  const { profileName } = useParams<{ profileName: string }>();
  const [hideSpotlights, setHideSpotlights] = useState(true);
  const [gradientStart, setGradientStart] = useState<string | null>(null);
  const [gradientEnd, setGradientEnd] = useState<string | null>(null);

  const { track } = useAnalytics();
  const { isMyProfile } = usePageType();
  const isDark = useTheme();
  const { userId, loading } = useGetUserId(profileName);
  const { isGuideTour } = useGuideTour();
  const { tutorialStatus, loading: settingsLoading } = useUserSettings();
  const { totalCount } = useGetTotalCountCards(
    userId ?? '',
    {
      typeIn: [CardTypeOptions.Card, CardTypeOptions.Collection],
      withPrivacy: true,
    },
    EQueryFetchPolicy.CacheAndNetwork
  );

  const { userData, loading: profileLoading } = useProfile(
    userId,
    EQueryFetchPolicy.CacheFirst,
    EQueryFetchPolicy.CacheFirst
  );

  const showNoContentText = userData && !totalCount;

  useEffect(() => {
    track(ANALYTICS_EVENTS.OpenProfilePage, {
      isOwnProfilePage: isMyProfile,
    });
  }, [track, isMyProfile]);

  return (
    <StyledProfileContainer gradientStart={gradientStart} gradientEnd={gradientEnd} isDark={isDark}>
      {tutorialStatus === ETutorialStatus.NotStarted && !settingsLoading && !isGuideTour && <AppTour />}
      <ProfileTop
        userData={userId ? userData : undefined}
        loading={profileLoading || loading}
        setHideSpotlights={setHideSpotlights}
        hideSpotlights={hideSpotlights}
        onGradientChange={(start: string | null, end: string | null) => {
          setGradientStart(start);
          setGradientEnd(end);
        }}
      />
      {!profileLoading && !hideSpotlights && !!userId ? (
        <>
          {showNoContentText && (
            <StyledNoContent data-testid={TEST_PROFILE_NO_PICKS_TITLE_LABEL} isDark={isDark}>
              {isMyProfile ? (
                <>
                  You haven&apos;t added any picks yet. Get started by exploring what others are adding on the{' '}
                  <a href={ROUTE_DISCOVER_FEATURED}>Featured feed</a>
                </>
              ) : (
                <>This user hasn&apos;t added any picks yet.</>
              )}
            </StyledNoContent>
          )}
        </>
      ) : (
        <StyledImageWrap>
          <img src={BlankCard} alt="loading" />
        </StyledImageWrap>
      )}
    </StyledProfileContainer>
  );
};
export default ProfilePage;
